import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import { FlightsFareTypes } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { useLowestFlightPrice } from 'tripPlanner/hooks/api/flights'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import GeoContext from 'contexts/geoContext'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'
import TextButton from 'LuxKit/Button/TextButton'
import ItemPriceAndCTAs from '../DetailItemCard/common/ItemPriceAndCTAs'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { useAppSelector } from 'hooks/reduxHooks'
import { isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import SelectFlightType from '../TripModal/TripItems/Variety/SelectFlightType'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CurrencyContext from 'contexts/currencyContext'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: FlightItem
}

function SuggestedFlightSearchPriceAndCTAs({ item }: Props) {
  const intl = useIntl()
  const { fareTypeLabelMap } = useFlightFareLabels()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const openModal = useOpenModal()
  const standaloneFlightsEnabled = useAppSelector(isStandaloneFlightsEnabled)
  const canEdit = useCanEditTrip()

  const {
    tripId,
    startDate,
    endDate,
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      currencyCode,
      regionCode,
      adultPrice,
      fareType,
    } = {},
  } = item

  const effectiveCurrencyCode = currencyCode ?? currentCurrency
  const { data: price, isLoading: priceLoading } = useLowestFlightPrice({
    originAirportCode,
    destinationAirportCode,
    currencyCode: effectiveCurrencyCode,
    regionCode: regionCode ?? currentRegionCode,
    shouldQuery: !adultPrice && fareType === FlightsFareTypes.RETURN,
    checkIn: startDate?.format(ISO_DATE_FORMAT),
    checkOut: endDate?.format(ISO_DATE_FORMAT),
    nights: (startDate && endDate) ? endDate.diff(startDate, 'days') : undefined,
  })

  const minAdultPrice = adultPrice ?? price
  const showPrice = !priceLoading && !!minAdultPrice

  const fareTypeLabel = fareType ? fareTypeLabelMap[fareType] : undefined

  const source = useTripClientSource()
  const flightSearchUrl = item.savedItemData ? addQueryParamsToPath(
    buildStandaloneFlightSearchURL(item.savedItemData),
    {
      source,
    },
  ) : undefined

  const onClickAddMyOwn = useCallback(() => {
    if (standaloneFlightsEnabled) {
      openModal(SelectFlightType, { tripId, defaultDate: startDate?.format(ISO_DATE_FORMAT) })
    } else {
      openModal(
        SelectFlightType,
        { tripId, customOnly: true },
        { size: 'S' },
      )
    }
  }, [startDate, tripId, openModal, standaloneFlightsEnabled])

  const fareTypeFlightsFromLabel = intl.formatMessage(
    {
      id: 'GvSW0Kixi58czkiBEM_d',
      defaultMessage: '{fareType} flights from',
      description: 'Label showing fare type followed by "flights from" before the price',
    },
    {
      fareType: fareTypeLabel,
    },
  )

  return (
    <ItemPriceAndCTAs>
      {showPrice && <div className="price-area">
        <CurrencyContext.Provider value={effectiveCurrencyCode}>
          <PriceRowPriceCaption>{fareTypeFlightsFromLabel}</PriceRowPriceCaption>
          <PriceRowPrice price={minAdultPrice} size="S" saleUnit="person" />
        </CurrencyContext.Provider>
      </div>}
      <div className="cta-area">
        <TextButton kind="tertiary" onClick={onClickAddMyOwn}>
          {intl.formatMessage({
            id: '3lGVF-lhV6CKE9Xy_39W',
            defaultMessage: 'Add my own',
            description: 'Button to add a custom flight to the trip in the flights flow',
          })}
        </TextButton>
        {flightSearchUrl && <TextButton
          kind="secondary"
          to={addTripIdParam(flightSearchUrl, tripId, canEdit)}
          target="_blank"
        >
          {intl.formatMessage({
            id: 'J4i063e6yOwvgqqv2yS6',
            defaultMessage: 'Search flights',
            description: 'Button text to initiate a flight search from the flights search form interface',
          })}
        </TextButton>}
      </div>
    </ItemPriceAndCTAs>
  )
}

export default SuggestedFlightSearchPriceAndCTAs
